import { html, isServer, nothing, unsafeCSS, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import EkoElement from "@/utilities/element";
import style from "./list-item.css?inline";
import "@/components/icon";

@customElement("eko-list-item")
class ListItem extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private _observer?: globalThis.MutationObserver;

  @property({ reflect: true }) icon?: string;

  protected firstUpdated() {
    // Re-render after hydration to pick up inherited icons from parent list
    if (!this.icon && this.getEffectiveIcon()) {
      this.requestUpdate();
    }
  }

  private getEffectiveIcon(): string | null {
    if (this.icon) {
      return this.icon;
    }

    // Skip parent lookup during SSR and hydration (first render) so the
    // client output matches the server output. firstUpdated() will trigger
    // a proper re-render to pick up inherited icons afterward.
    if (isServer || !this.hasUpdated) {
      return null;
    }

    const list = this.closest("eko-list") as any;
    const icon = list?.icon || list?.getAttribute("icon");

    return icon ?? null;
  }

  private renderIcon(): TemplateResult | typeof nothing {
    const icon = this.getEffectiveIcon();

    if (!icon) {
      return nothing;
    }

    return html`<eko-icon
      class="eko-list-item__icon"
      name=${icon}
      size="sm"
      part="icon"></eko-icon>`;
  }

  connectedCallback() {
    super.connectedCallback();

    const parent = this.closest("eko-list");

    if (!parent) return;

    this._observer = new globalThis.MutationObserver(() => this.requestUpdate());

    this._observer.observe(parent, {
      attributes: true,
      attributeFilter: ["icon"],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this._observer?.disconnect();
  }

  render() {
    return html`<li class="eko-list-item" part="root">
      ${this.renderIcon()}
      <span class="eko-list-item__content" part="content">
        <slot></slot>
      </span>
    </li>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-list-item": ListItem;
  }
}

export default ListItem;
